import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { BillingInfo } from 'app/models/billing-info';
import { SuperScript } from 'app/models/super-script';
import { BillingService } from 'app/services/billing.service';
import { UserService } from 'app/services/user.service';
import { TabsetComponent, ToastService } from 'ng-uikit-pro-standard';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-subscription-billing',
  templateUrl: './subscription-billing.component.html',
  styleUrls: ['./subscription-billing.component.scss']
})
export class SubscriptionBillingComponent implements OnInit, AfterViewInit {

  @ViewChild('annualMonthlyTabs') annualMonthlyTabs: TabsetComponent;

  formsAnnual: string = 'free';
  filesAnnual: string = 'free';
  workflowsAnnual: string = 'free';
  dashboardsAnnual: string = 'free';

  formsMonthly: string = 'free';
  filesMonthly: string = 'free';
  workflowsMonthly: string = 'free';
  dashboardsMonthly: string = 'free';

  isInvoiceProcessor: boolean = false;
  invoiceProcessorNumPages: number = 1000;

  isLoading: boolean = false;
  isLoadingBillingInfo: boolean = false;

  isChangeSubscription: boolean = false;

  billingInfo: BillingInfo;

  companyName: string = '';

  superScripts: SuperScript[] = [];

  constructor(public userService: UserService, public billingService: BillingService,
            public toastService: ToastService) {
  }

  ngOnInit(): void {
    this.formsAnnual = 'free';
    this.filesAnnual = 'free';
    this.workflowsAnnual = 'free';
    this.dashboardsAnnual = 'free';        

    this.formsMonthly = 'free';
    this.filesMonthly = 'free';
    this.workflowsMonthly = 'free';
    this.dashboardsMonthly = 'free';   
    
    this.userService.currentUser.subscribe(user => {
      this.syncOptionsWithUserSubscription(user);

      if (user.subscription_status.is_subscribed) {
        this.refreshBillingInfo();
      }
    });

    // refresh current user to get the updated quota usages
    this.userService.getCurrent().subscribe();
  }

  ngAfterViewInit(): void {
  }

  refreshBillingInfo() {
    this.isLoadingBillingInfo = true
    this.billingService.info().pipe(finalize(() => {
      this.isLoadingBillingInfo = false;
    })).subscribe((b) => {
      this.billingInfo = b;
      this.companyName = b.company_name;
    });
  }

  syncOptionsWithUserSubscription(user) {
    this.formsAnnual = 'free';
    this.filesAnnual = 'free';
    this.workflowsAnnual = 'free';
    this.dashboardsAnnual = 'free';        

    this.formsMonthly = 'free';
    this.filesMonthly = 'free';
    this.workflowsMonthly = 'free';
    this.dashboardsMonthly = 'free';  

    if (user.subscription_status.is_subscribed) {
      (user.subscription_status.subscribed_features || []).forEach((f) => {
        if (f.internal_name.includes('forms')) {
          this.formsAnnual = f.internal_name;
          this.formsMonthly = f.internal_name;
        } else if (f.internal_name.includes('files')) {
          this.filesAnnual = f.internal_name;
          this.filesMonthly = f.internal_name;
        } else if (f.internal_name.includes('workflows')) {
          this.workflowsAnnual = f.internal_name;
          this.workflowsMonthly = f.internal_name;
        } else if (f.internal_name.includes('dashboards')) {
          this.dashboardsAnnual = f.internal_name;
          this.dashboardsMonthly = f.internal_name;
        } else if (f.internal_name.includes('all_features.addons.ai_invoice_processor')) {
          this.isInvoiceProcessor = true;
          this.invoiceProcessorNumPages = f.current_quota;
        }
      });

      (user.subscription_status.super_scripts || []).forEach((s) => {
        if (s.is_subscribed) {
          user.subscription_status.subscribed_features.push({
            internal_name: 'all_features.addons.super_scripts.' + s.function_name,
            display_name: s.display_name,
            current_usage: s.current_usage,
            current_quota: s.subscribed_amount + s.free_amount
          });

          user.subscription_status.usage_features.push({
            internal_name: 'all_features.addons.super_scripts.' + s.function_name,
            display_name: s.display_name,
            current_usage: s.current_usage,
            current_quota: s.subscribed_amount + s.free_amount
          });
        }
      });
    }
    
    this.superScripts = user.subscription_status.super_scripts || [];
  }

  isAddOnsEnabled() {
    let result: boolean = this.isInvoiceProcessor;
    this.superScripts.forEach((s) => {
      result = result || s.enabled;
    });

    return result;
  }
  getAnnualAddOnsPrice() {
    let result: number = this.getAnnualInvoiceProcessorPrice();
    this.superScripts.forEach((s) => {
      if (s.enabled) {
        result += ((s.annual_price_per_call / 100) * s.subscribed_amount / 12);
      }
    });

    return result;
  }

  getMonthlyAddOnsPrice() {
    let result: number = this.getMonthlyInvoiceProcessorPrice();
    this.superScripts.forEach((s) => {
      if (s.enabled) {
        result += (s.monthly_price_per_call / 100) * s.subscribed_amount;
      }
    });

    return result;
  }

  getAnnualInvoiceProcessorPrice() {
    if (!this.isInvoiceProcessor || this.invoiceProcessorNumPages < 1) {
      return 0;
    }

    let unitPrice: number = 10.80;
    if (this.invoiceProcessorNumPages >= 2500 && this.invoiceProcessorNumPages < 5000) {
      unitPrice = 10.26;
    } else if (this.invoiceProcessorNumPages >= 5000 && this.invoiceProcessorNumPages < 10000) {
      unitPrice = 9.72;
    } else if (this.invoiceProcessorNumPages >= 10000) {
      unitPrice = 9.18;
    }

    return unitPrice * this.invoiceProcessorNumPages / 12;
  }

  getMonthlyInvoiceProcessorPrice() {
    if (!this.isInvoiceProcessor || this.invoiceProcessorNumPages < 1) {
      return 0;
    }

    let unitPrice: number = 1;
    if (this.invoiceProcessorNumPages >= 2500 && this.invoiceProcessorNumPages < 5000) {
      unitPrice = 0.95;
    } else if (this.invoiceProcessorNumPages >= 5000 && this.invoiceProcessorNumPages < 10000) {
      unitPrice = 0.90;
    } else if (this.invoiceProcessorNumPages >= 10000) {
      unitPrice = 0.85;
    }

    return unitPrice * this.invoiceProcessorNumPages;
  }

  submitCompanyName() {
    this.isLoading = true;
    this.billingService.changeCompanyName(this.companyName).pipe(finalize(() => {
      this.isLoading = false;
    })).subscribe(() => {
      this.toastService.success("Changed company name!");
    }, () => {
      this.toastService.error('Error changing company name.');
    });
  }

  addPaymentMethod() {
    this.isLoading = true;
    this.billingService.checkoutAddPaymentMethod().subscribe((r) => {
      window.location.href = r.checkout_url;
    }, () => {
      this.isLoading = false;
      this.toastService.error('Error starting add payment method process, please try again later or contact EcoDocs support.');
    });
  }

  setPaymentMethodAsDefault(paymentMethodId: string) {
    this.isLoading = true;
    this.billingService.setPaymentMethodAsDefault(paymentMethodId).pipe(finalize(() => {
      this.isLoading = false;
    })).subscribe(() => {
      this.toastService.success('Default payment method changed!');
      this.refreshBillingInfo();
    }, () => {
      this.toastService.error('Error changing default payment method.');
    });
  }

  removePaymentMethod(paymentMethodId: string) {
    this.isLoading = true;
    this.billingService.deletePaymentMethod(paymentMethodId).pipe(finalize(() => {
      this.isLoading = false;
    })).subscribe(() => {
      this.toastService.success('Payment method deleted!');
      this.refreshBillingInfo();
    }, () => {
      this.toastService.error('Error deleting payment method.');
    });
  }

  scrollToFeaturesDetails(event: Event) {
    event.preventDefault();
    document.querySelector('.features-details').scrollIntoView({behavior: 'smooth'});
  }

  scrollToExtraQuotasPrices(event: Event) {
    event.preventDefault();
    document.querySelector('.extra-quotas-prices').scrollIntoView({behavior: 'smooth'});
  }

  subscribeAnnual() {
    if (this.isInvoiceProcessor && this.invoiceProcessorNumPages < 1) {
      return;   // invalid
    }

    let features: any = {};
    if (this.formsAnnual != 'free') {
      features[this.formsAnnual] = 0;
    }
    if (this.filesAnnual != 'free') {
      features[this.filesAnnual] = 0;
    }
    if (this.workflowsAnnual != 'free') {
      features[this.workflowsAnnual] = 0;
    }
    if (this.dashboardsAnnual != 'free') {
      features[this.dashboardsAnnual] = 0;
    }
    if (this.isInvoiceProcessor) {
      features['all_features.addons.ai_invoice_processor'] = this.invoiceProcessorNumPages;
    }

    this.isLoading = true;
    if (this.isChangeSubscription) {
      this.billingService.changeSubscription(features, true).subscribe(() => {
        /** @todo redirect to billing succeeded page and display subscription is pending update or something like that, give it a few minutes for the webhook to arrive */
        this.toastService.success('Subscription changed!');
        this.userService.getCurrent().pipe((finalize(() => {
          this.isLoading = false;
          this.isChangeSubscription = false;
        }))).subscribe();
      }, () => {
        this.isLoading = false;
        this.toastService.error('Error changing subscription, please try again later or contact EcoDocs support.');
      });
    } else {
      this.billingService.checkout(features, true).subscribe((r) => {
        window.location.href = r.checkout_url;
      }, () => {
        this.isLoading = false;
        this.toastService.error('Error starting checkout process, please try again later or contact EcoDocs support.');
      });
    }
  }

  subscribeMonthly() {
    if (this.isInvoiceProcessor && this.invoiceProcessorNumPages < 1) {
      return;   // invalid
    }

    let features: any = {};
    if (this.formsMonthly != 'free') {
      features[this.formsMonthly] = 0;
    }
    if (this.filesMonthly != 'free') {
      features[this.filesMonthly] = 0;
    }
    if (this.workflowsMonthly != 'free') {
      features[this.workflowsMonthly] = 0;
    }
    if (this.dashboardsMonthly != 'free') {
      features[this.dashboardsMonthly] = 0;
    }
    if (this.isInvoiceProcessor) {
      features['all_features.addons.ai_invoice_processor'] = this.invoiceProcessorNumPages;
    }

    this.isLoading = true;
    if (this.isChangeSubscription) {
      this.billingService.changeSubscription(features, false).subscribe(() => {
        this.toastService.success('Subscription changed!');
        this.userService.getCurrent().pipe((finalize(() => {
          this.isLoading = false;
          this.isChangeSubscription = false;
        }))).subscribe();
      }, () => {
        this.isLoading = false;
        this.toastService.error('Error changing subscription, please try again later or contact EcoDocs support.');
      });
    } else {
      this.billingService.checkout(features, false).subscribe((r) => {
        window.location.href = r.checkout_url;
      }, () => {
        this.isLoading = false;
        this.toastService.error('Error starting checkout process, please try again later or contact EcoDocs support.');
      });
    }
  }  

  annualTotalBeforeDiscount() {
    let formsPrice: number = 0;
    if (this.formsAnnual == 'all_features.forms.basic') {
      formsPrice = 250 / 12;
    } else if (this.formsAnnual == 'all_features.forms.advanced') {
      formsPrice = 350 / 12;
    } else if (this.formsAnnual == 'all_features.forms.premium') {
      formsPrice = 390 / 12;
    }
    
    let filesPrice: number = 0;
    if (this.filesAnnual == 'all_features.files.basic') {
      filesPrice = 190 / 12;
    } else if (this.filesAnnual == 'all_features.files.advanced') {
      filesPrice = 250 / 12;
    } 
    
    let workflowsPrice: number = 0;
    if (this.workflowsAnnual == 'all_features.workflows.basic') {
      workflowsPrice = 290 / 12;
    } else if (this.workflowsAnnual == 'all_features.workflows.advanced') {
      workflowsPrice = 390 / 12;
    } 

    let dashboardsPrice: number = 0;
    if (this.dashboardsAnnual == 'all_features.dashboards.basic') {
      dashboardsPrice = 190 / 12;
    } else if (this.dashboardsAnnual == 'all_features.dashboards.advanced') {
      dashboardsPrice = 290 / 12;
    }

    return formsPrice + filesPrice + workflowsPrice + dashboardsPrice;
  }

  monthlyTotalBeforeDiscount() {
    let formsPrice: number = 0;
    if (this.formsMonthly == 'all_features.forms.basic') {
      formsPrice = 25;
    } else if (this.formsMonthly == 'all_features.forms.advanced') {
      formsPrice = 35;
    } else if (this.formsMonthly == 'all_features.forms.premium') {
      formsPrice = 39;
    }
    
    let filesPrice: number = 0;
    if (this.filesMonthly == 'all_features.files.basic') {
      filesPrice = 19;
    } else if (this.filesMonthly == 'all_features.files.advanced') {
      filesPrice = 25;
    } 
    
    let workflowsPrice: number = 0;
    if (this.workflowsMonthly == 'all_features.workflows.basic') {
      workflowsPrice = 29;
    } else if (this.workflowsMonthly == 'all_features.workflows.advanced') {
      workflowsPrice = 39;
    } 

    let dashboardsPrice: number = 0;
    if (this.dashboardsMonthly == 'all_features.dashboards.basic') {
      dashboardsPrice = 19;
    } else if (this.dashboardsMonthly == 'all_features.dashboards.advanced') {
      dashboardsPrice = 29;
    }

    return formsPrice + filesPrice + workflowsPrice + dashboardsPrice;
  }

  annualDiscount() {

    let discountPercent: number = 0;

    if (this.userService.currentUserValue.subscription_status.annual_special_discount_percent) {
      discountPercent = this.userService.currentUserValue.subscription_status.annual_special_discount_percent / 100;
    } else {
      let numPaid: number = 0;

      if (this.formsAnnual != 'free') {
        numPaid++;
      }
      if (this.filesAnnual != 'free') {
        numPaid++;
      }
      if (this.workflowsAnnual != 'free') {
        numPaid++;
      }
      if (this.dashboardsAnnual != 'free') {
        numPaid++;
      }

      if (numPaid == 2) {
        discountPercent = 0.1;
      } else if (numPaid == 3) {
        discountPercent = 0.17;
      } else if (numPaid == 4) {
        discountPercent = 0.25;
      }
    }

    return discountPercent * this.annualTotalBeforeDiscount();
  }

  monthlyDiscount() {
    let discountPercent: number = 0;

    if (this.userService.currentUserValue.subscription_status.monthly_special_discount_percent) {
      discountPercent = this.userService.currentUserValue.subscription_status.monthly_special_discount_percent / 100;
    } else {
      let numPaid: number = 0;

      if (this.formsMonthly != 'free') {
        numPaid++;
      }
      if (this.filesMonthly != 'free') {
        numPaid++;
      }
      if (this.workflowsMonthly != 'free') {
        numPaid++;
      }
      if (this.dashboardsMonthly != 'free') {
        numPaid++;
      }

      if (numPaid == 2) {
        discountPercent = 0.1;
      } else if (numPaid == 3) {
        discountPercent = 0.17;
      } else if (numPaid == 4) {
        discountPercent = 0.25;
      }
    }

    return discountPercent * this.monthlyTotalBeforeDiscount();
  }
  callNow(){
    window.location.href = "tel:+18559108235";
  }
}
